import { Megaphone, Plus } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../../components/atoms/button/button';
import { EmptyState } from '../../components/molecules/empty-state/empty-state';
import { ErrorState } from '../../components/molecules/error-state/error-state';
import { Pagination } from '../../components/molecules/pagination/pagination';
import { useAuth } from '../../hooks/use-auth';
import { useAvisos } from '../../hooks/use-avisos';
import {
  activateAvisoThunk,
  createAvisoThunk,
  deactivateAvisoThunk,
  updateAvisoThunk,
} from '../../store/slices/avisos.slice';
import {
  CreateAvisoPayload,
  UpdateAvisoPayload,
} from '../../types/aviso';
import { AvisoDetailDrawer } from './components/aviso-detail-drawer';
import { AvisoFormDrawer } from './components/aviso-form-drawer/aviso-form-drawer';
import { AvisosCards } from './components/avisos-cards';
import { AvisosFiltersBar } from './components/avisos-filters';
import { AvisosTable } from './components/avisos-table';
import { ConfirmarDesativarModal } from './components/confirmar-desativar-modal';
import {
  CardsWrapper,
  Header,
  HeaderText,
  Page,
  SkeletonRow,
  Subtitle,
  TableWrapper,
  Title,
} from './avisos.styles';

export const AvisosPage = () => {
  const { hasPermission } = useAuth();
  const {
    items,
    page,
    limit,
    total,
    totalPages,
    filters,
    status,
    error,
    selectedDetail,
    detailStatus,
    detailError,
    mutating,
    mutationError,
    refresh,
    setFilters,
    clearFilters,
    setPage,
    setLimit,
    openDetail,
    closeDetail,
    create,
    update,
    activate,
    deactivate,
    clearMutationError,
  } = useAvisos();

  const canCreate = hasPermission('AVISO_CREATE');
  const canUpdate = hasPermission('AVISO_UPDATE');
  const canActivate = hasPermission('AVISO_ACTIVATE');
  const canDeactivate = hasPermission('AVISO_DEACTIVATE');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const isLoading = status === 'loading';
  const hasError = status === 'error';
  const isEmpty = !isLoading && !hasError && items.length === 0;

  const drawerOpen =
    selectedDetail != null ||
    detailStatus === 'loading' ||
    detailStatus === 'error';

  const handleCreate = async (
    payload: CreateAvisoPayload | UpdateAvisoPayload,
  ) => {
    const result = await create(payload as CreateAvisoPayload);
    if (createAvisoThunk.fulfilled.match(result)) {
      toast.success('Aviso criado');
      setCreateOpen(false);
      refresh();
    } else {
      toast.error('Não foi possível criar o aviso');
    }
  };

  const handleEdit = async (
    payload: CreateAvisoPayload | UpdateAvisoPayload,
  ) => {
    if (!selectedDetail) return;
    const result = await update(selectedDetail.id, payload as UpdateAvisoPayload);
    if (updateAvisoThunk.fulfilled.match(result)) {
      toast.success('Aviso atualizado');
      setEditOpen(false);
      refresh();
    } else {
      toast.error('Não foi possível atualizar o aviso');
    }
  };

  const handleActivate = async () => {
    if (!selectedDetail) return;
    const result = await activate(selectedDetail.id);
    if (activateAvisoThunk.fulfilled.match(result)) {
      toast.success('Aviso ativado');
      refresh();
    } else {
      toast.error('Não foi possível ativar o aviso');
    }
  };

  const handleDeactivate = async () => {
    if (!selectedDetail) return;
    const result = await deactivate(selectedDetail.id);
    if (deactivateAvisoThunk.fulfilled.match(result)) {
      toast.success('Aviso desativado');
      setDeactivateOpen(false);
      refresh();
    } else {
      toast.error('Não foi possível desativar o aviso');
    }
  };

  const isMutatingDetail =
    mutating?.id === selectedDetail?.id &&
    (mutating?.action === 'activate' || mutating?.action === 'deactivate');

  return (
    <Page>
      <Header>
        <HeaderText>
          <Title>Avisos da plataforma</Title>
          <Subtitle>
            Crie, edite e programe avisos exibidos na plataforma do cliente.
            Avisos só ficam visíveis dentro da janela de datas configurada.
          </Subtitle>
        </HeaderText>
        {canCreate && (
          <Button
            variant="primary"
            startIcon={<Plus size={14} />}
            onClick={() => {
              clearMutationError();
              setCreateOpen(true);
            }}
            data-testid="open-create-aviso"
          >
            Novo aviso
          </Button>
        )}
      </Header>

      <AvisosFiltersBar
        initialValues={filters}
        onApply={setFilters}
        onClear={clearFilters}
      />

      {isLoading && (
        <div data-testid="avisos-loading">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {hasError && (
        <ErrorState
          title="Não foi possível carregar os avisos"
          description={error ?? 'Tente novamente em alguns instantes.'}
          action={
            <Button variant="secondary" onClick={() => refresh()}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {isEmpty && (
        <EmptyState
          icon={<Megaphone size={26} />}
          title="Nenhum aviso cadastrado"
          description="Crie um aviso para comunicar usuários da plataforma."
          action={
            canCreate ? (
              <Button variant="secondary" onClick={() => setCreateOpen(true)}>
                Novo aviso
              </Button>
            ) : undefined
          }
        />
      )}

      {!isLoading && !hasError && items.length > 0 && (
        <>
          <TableWrapper>
            <AvisosTable items={items} onOpen={openDetail} />
          </TableWrapper>
          <CardsWrapper>
            <AvisosCards items={items} onOpen={openDetail} />
          </CardsWrapper>
          <Pagination
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </>
      )}

      <AvisoDetailDrawer
        isOpen={drawerOpen}
        detail={selectedDetail}
        status={detailStatus}
        errorMessage={detailError}
        canUpdate={canUpdate}
        canActivate={canActivate}
        canDeactivate={canDeactivate}
        activating={isMutatingDetail}
        onClose={() => {
          closeDetail();
          clearMutationError();
        }}
        onEdit={() => {
          clearMutationError();
          setEditOpen(true);
        }}
        onActivate={handleActivate}
        onDeactivate={() => {
          clearMutationError();
          setDeactivateOpen(true);
        }}
      />

      <AvisoFormDrawer
        isOpen={createOpen}
        mode="create"
        saving={mutating?.action === 'create'}
        errorMessage={mutating?.action === 'create' ? null : mutationError}
        onSubmit={handleCreate}
        onClose={() => {
          setCreateOpen(false);
          clearMutationError();
        }}
      />

      <AvisoFormDrawer
        isOpen={editOpen}
        mode="edit"
        aviso={selectedDetail}
        saving={
          mutating?.action === 'update' && mutating?.id === selectedDetail?.id
        }
        errorMessage={mutating?.action === 'update' ? null : mutationError}
        onSubmit={handleEdit}
        onClose={() => {
          setEditOpen(false);
          clearMutationError();
        }}
      />

      <ConfirmarDesativarModal
        isOpen={deactivateOpen}
        titulo={selectedDetail?.titulo ?? ''}
        loading={isMutatingDetail && mutating?.action === 'deactivate'}
        onConfirm={handleDeactivate}
        onClose={() => setDeactivateOpen(false)}
      />
    </Page>
  );
};
